import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Card, CardBody, Col, Row, UncontrolledPopover } from 'reactstrap';

type TileSelectorOption = {
  id: string;
  name: string;
  description?: string;
  hoverHelperText?: React.ReactNode;
};

type Props = {
  value?: string;
  onChange: (value: string) => void;
  onValidChange?: (validationMessage?: string) => void;
  required?: boolean;
  options: TileSelectorOption[];
  className?: string;
};

const TileSelectorInput: FC<Props> = ({
  value,
  onChange,
  onValidChange,
  required,
  options,
  className,
}) => {
  const { formatMessage } = useIntl();

  const [validationMessage, setValidationMessage] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (required) {
      const isValid = value != null;
      setValidationMessage(
        isValid
          ? undefined
          : formatMessage({
              id: 'app.widgets.inputs.tile_selector_input.please_select_an_answer',
              defaultMessage: 'Please select an answer.',
            })
      );
    } else {
      setValidationMessage(undefined);
    }
  }, [value, required, formatMessage]);

  useEffect(() => {
    if (onValidChange) {
      onValidChange(validationMessage);
    }
  }, [validationMessage, onValidChange]);

  const handleOnChange = useCallback(
    (option) => {
      onChange(option);
    },
    [onChange]
  );

  return (
    <Row className={'mb-n4 ' + (className || '')}>
      {options.map((option, index) => (
        <Col
          key={index}
          className="col-12 col-md-6 py-2 py-md-0 mb-4"
          onClick={() => handleOnChange(option.id)}
        >
          <Card
            id={'tile-option-' + option.id}
            className="mb-0 align-items-start lift h-100"
            role="button"
            style={{
              border: value === option.id ? '1px solid #2c7be5' : '',
            }}
          >
            <CardBody>
              <h4 className="">{option.name}</h4>
              <p className="small text-muted mb-0">{option.description}</p>
            </CardBody>
          </Card>
          {option.hoverHelperText && (
            <UncontrolledPopover
              placement="top"
              trigger="hover"
              target={'tile-option-' + option.id}
            >
              {option.hoverHelperText}
            </UncontrolledPopover>
          )}
        </Col>
      ))}
    </Row>
  );
};

export default React.memo(TileSelectorInput);
